import React, { useState, useEffect } from 'react';
import { Users, ChevronRight, Gift } from 'lucide-react';
import { useWebSocket } from './hooks/useWebSocket';
import { MOCK_GIFTS } from './utils/constants';
import TelegramAuth from './components/TelegramAuth';
import Header from './components/Header';
import Roulette from './components/Roulette';
import Inventory from './components/Inventory';
import History from './components/History';

// ЗАМЕНИТЕ на ваш username бота (без @)
const BOT_USERNAME = 'ticketgiftbot';

function App() {
  const [userId, setUserId] = useState(null);
  const [telegramUser, setTelegramUser] = useState(null);
  const [isTelegramApp, setIsTelegramApp] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [currentNav, setCurrentNav] = useState('roulette');

  const {
    connected,
    gameState,
    user,
    history,
    inventory,
    error,
    countdown,
    winner,
    placeGift,
    getHistory,
    getInventory
  } = useWebSocket(userId);

  useEffect(() => {
    // Проверяем, открыто ли приложение в Telegram
    const tg = window.Telegram?.WebApp;
    
    if (tg && tg.initData && tg.initData !== '') {
      // Приложение открыто в Telegram
      setIsTelegramApp(true);
      tg.ready();
      tg.expand();
      
      const tgUser = tg.initDataUnsafe?.user;
      if (tgUser) {
        setTelegramUser(tgUser);
        setUserId(tgUser.id.toString());
      }
    } else {
      // Приложение открыто НЕ в Telegram
      setIsTelegramApp(false);
      
      // Для разработки: раскомментируйте следующие строки
      // setIsTelegramApp(true);
      // setUserId('test_user_' + Math.random().toString(36).substr(2, 9));
      // setTelegramUser({ username: 'TestUser', first_name: 'Test' });
    }
  }, []);

  // Если не в Telegram - показываем блокировку
  if (!isTelegramApp) {
    return <TelegramAuth botUsername={BOT_USERNAME} />;
  }

  const handleShowInventory = () => {
    getInventory();
    setShowInventory(true);
  };

  const handleShowHistory = () => {
    getHistory();
    setShowHistory(true);
  };

  const getGiftById = (id) => {
    return MOCK_GIFTS.find(g => g.id === id) || inventory.find(g => g.id === id);
  };

  const participants = gameState?.participants 
    ? Object.entries(gameState.participants).map(([uid, p]) => ({
        userId: uid,
        username: uid,
        avatar: '👤',
        tickets: p.tickets,
        percentage: p.percentage
      }))
    : [];

  if (!userId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-purple-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-purple-900 text-white pb-20">
      {/* Header */}
      <Header user={user} telegramUser={telegramUser} />

      {/* Navigation */}
      <div className="bg-blue-800/30 backdrop-blur-sm flex justify-around py-3 text-sm sticky top-0 z-30" style={{ top: '72px' }}>
        <button 
          className={currentNav === 'cases' ? 'font-bold border-b-2 border-yellow-400' : 'opacity-50'}
          onClick={() => setCurrentNav('cases')}
        >
          Кейсы
        </button>
        <button 
          className={currentNav === 'market' ? 'font-bold border-b-2 border-yellow-400' : 'opacity-50'}
          onClick={() => setCurrentNav('market')}
        >
          Маркет
        </button>
        <button 
          className={currentNav === 'roulette' ? 'font-bold border-b-2 border-yellow-400' : 'opacity-50'}
          onClick={() => setCurrentNav('roulette')}
        >
          Рулетка
        </button>
        <button 
          className={currentNav === 'friends' ? 'font-bold border-b-2 border-yellow-400' : 'opacity-50'}
          onClick={() => setCurrentNav('friends')}
        >
          Друзья
        </button>
        <button 
          className={currentNav === 'profile' ? 'font-bold border-b-2 border-yellow-400' : 'opacity-50'}
          onClick={() => setCurrentNav('profile')}
        >
          Профиль
        </button>
      </div>

      {/* Connection Status */}
      {!connected && (
        <div className="bg-orange-500/80 text-white px-4 py-2 text-center text-sm">
          Переподключение к серверу...
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/80 text-white px-4 py-2 text-center text-sm">
          {error}
        </div>
      )}

      {/* Main Content - Roulette */}
      {currentNav === 'roulette' && (
        <>
          {/* Game Info */}
          <div className="px-4 py-3 flex justify-between items-center">
            <span className="text-sm">
              Идёт игра #{gameState?.number || 1}
            </span>
            <button 
              onClick={handleShowHistory}
              className="flex items-center gap-1 text-sm text-blue-300 hover:text-blue-200 transition"
            >
              История игр <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Roulette Component */}
          <Roulette 
            gameState={gameState}
            participants={participants}
            countdown={countdown}
            winner={winner}
          />

          {/* Game Stats */}
          <div className="px-4 mb-4 grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-300 mb-1">Поставили</div>
              <div className="font-bold flex items-center gap-1">
                <Users className="w-4 h-4" />
                {gameState?.participantCount || 0} игроков
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-300 mb-1">В банке</div>
              <div className="font-bold flex items-center gap-1">
                <Gift className="w-4 h-4 text-yellow-400" />
                {gameState?.totalBank?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div>
              <button 
                onClick={handleShowInventory}
                disabled={!gameState?.canJoin}
                className={`w-full py-2 px-3 rounded-lg font-medium text-sm transition ${
                  gameState?.canJoin 
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                    : 'bg-gray-500 opacity-50 cursor-not-allowed'
                }`}
              >
                Выбрать подарок
              </button>
            </div>
          </div>

          {/* Current Participants */}
          {participants.length > 0 && (
            <div className="px-4 mb-4">
              <h3 className="text-sm font-medium mb-2 text-gray-300">Участники:</h3>
              <div className="bg-blue-800/30 rounded-xl p-3 space-y-2">
                {participants.map((p) => (
                  <div key={p.userId} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm">
                        {p.avatar}
                      </div>
                      <span className="text-sm">@{p.username}</span>
                    </div>
                    <div className="text-sm text-yellow-400 font-medium">
                      {p.percentage?.toFixed(2)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Profile Tab */}
      {currentNav === 'profile' && (
        <div className="px-4 py-6">
          <h2 className="text-2xl font-bold mb-4">Профиль</h2>
          
          <div className="bg-blue-800/30 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-2xl overflow-hidden">
                {telegramUser?.photo_url ? (
                  <img 
                    src={telegramUser.photo_url} 
                    alt="Avatar" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  '👤'
                )}
              </div>
              <div>
                <h3 className="font-bold text-lg">
                  @{telegramUser?.username || user?.username}
                </h3>
                {telegramUser?.first_name && (
                  <p className="text-sm text-gray-300">
                    {telegramUser.first_name} {telegramUser.last_name || ''}
                  </p>
                )}
                <p className="text-xs text-gray-400">ID: {userId}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-blue-700/50 rounded-lg p-3">
                <div className="text-2xl font-bold flex items-center justify-center gap-2">
                  <Gift className="w-6 h-6 text-yellow-400" />
                  {user?.balance?.toFixed(2) || '0.00'}
                </div>
                <div className="text-xs text-gray-300 mt-1">Баланс</div>
              </div>
              <div className="bg-blue-700/50 rounded-lg p-3">
                <div className="text-2xl font-bold">{user?.inventory?.length || 0}</div>
                <div className="text-xs text-gray-300 mt-1">Подарков</div>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-bold mb-3">Мои подарки</h3>
          <div className="bg-blue-800/30 rounded-xl overflow-hidden">
            {user?.inventory && user.inventory.length > 0 ? (
              <div className="divide-y divide-blue-700/50">
                {user.inventory.map((giftId, index) => {
                  const gift = getGiftById(giftId);
                  return gift ? (
                    <div key={index} className="flex items-center justify-between p-3 hover:bg-blue-700/30 transition">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 w-8">#{index + 1}</span>
                        <span className="text-2xl">{gift.icon}</span>
                        <span className="font-medium">{gift.name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-yellow-400 font-bold">
                        <Gift className="w-4 h-4" />
                        {gift.price.toFixed(2)} TON
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400">
                <Gift className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>У вас пока нет подарков</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Placeholder for other tabs */}
      {currentNav === 'cases' && (
        <div className="px-4 py-12 text-center">
          <div className="text-4xl mb-4">🎁</div>
          <h3 className="text-xl font-bold mb-2">Кейсы</h3>
          <p className="text-gray-300">Раздел в разработке</p>
        </div>
      )}

      {currentNav === 'market' && (
        <div className="px-4 py-12 text-center">
          <div className="text-4xl mb-4">🏪</div>
          <h3 className="text-xl font-bold mb-2">Маркет</h3>
          <p className="text-gray-300">Раздел в разработке</p>
        </div>
      )}

      {currentNav === 'friends' && (
        <div className="px-4 py-12 text-center">
          <div className="text-4xl mb-4">👥</div>
          <h3 className="text-xl font-bold mb-2">Друзья</h3>
          <p className="text-gray-300">Раздел в разработке</p>
        </div>
      )}

      {/* Winner Result Modal */}
      {winner && gameState?.status === 'finished' && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-gradient-to-b from-purple-600 to-blue-600 p-8 rounded-3xl text-center max-w-sm mx-4 animate-bounce-in">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold mb-4">Победитель!</h2>
            <div className="w-24 h-24 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-5xl mx-auto mb-4 ring-4 ring-yellow-400">
              👤
            </div>
            <h3 className="text-2xl font-bold mb-2">@{winner.username}</h3>
            <div className="text-4xl font-bold text-yellow-400 mb-2 flex items-center justify-center gap-2">
              <Gift className="w-10 h-10" />
              {winner.winAmount?.toFixed(2)}
            </div>
            <div className="text-xl mb-2">{winner.percentage?.toFixed(2)}%</div>
            <div className="text-sm text-gray-300">Билет #{winner.winningTicket}</div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showInventory && (
        <Inventory 
          gifts={inventory}
          onPlaceGift={placeGift}
          onClose={() => setShowInventory(false)}
        />
      )}

      {showHistory && (
        <History 
          games={history}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

export default App;